import React, { useCallback, useEffect, useRef } from "react";
import * as d3 from "d3";
import { getChartColors } from "../../helpers/getChartColors";
import { Box, Typography } from "@mui/material";
import EmptyComponent from "../EmptyComponent";
import CustomTooltip from "../CustomTooltip";

interface PieChartData {
  label: string;
  count: number;
  percentage: number;
}

interface PieChartProps {
  data: PieChartData[];
  title?: string;
  tooltipTitle?: string;
  showLegend?: boolean;
  labelSize?: number | string;
}

const colors = getChartColors();

const PieChart: React.FC<PieChartProps> = ({
  data,
  title,
  tooltipTitle,
  showLegend = true,
  labelSize = 13,
}) => {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const floatingTooltipRef = useRef<HTMLDivElement | null>(null);

  const percentFormatterRef = useRef(
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 })
  );
  const countFormatterRef = useRef(
    new Intl.NumberFormat(undefined, { maximumFractionDigits: 20 })
  );

  const showTooltip = useCallback(
    (d: d3.PieArcDatum<PieChartData>, clientX: number, clientY: number) => {
      const el = floatingTooltipRef.current;
      if (!el) return;

      const formatPercent = (v: unknown) => {
        const n = typeof v === "number" ? v : Number(v);
        return Number.isFinite(n) ? percentFormatterRef.current.format(n) : "0";
      };
      const formatCount = (v: unknown) => {
        const n = typeof v === "number" ? v : Number(v);
        return Number.isFinite(n) ? countFormatterRef.current.format(n) : "0";
      };

      el.innerText = `${formatPercent(d.data.percentage)}% (${formatCount(d.data.count)})`;
      el.style.display = "block";
      el.style.left = `${clientX + 0}px`;
      el.style.top = `${clientY - 30}px`;
    },
    []
  );

  const moveTooltip = useCallback((clientX: number, clientY: number) => {
    const el = floatingTooltipRef.current;
    if (!el || el.style.display === "none") return;
    el.style.left = `${clientX + 0}px`;
    el.style.top = `${clientY - 30}px`;
  }, []);

  const hideTooltip = useCallback(() => {
    const el = floatingTooltipRef.current;
    if (el) el.style.display = "none";
  }, []);

  useEffect(() => {
    if (!svgRef.current) return;

    const container = svgRef.current.parentElement;
    if (!container) return;

    const { width, height } = container.getBoundingClientRect();
    const size = Math.min(width, height);
    const radius = size / 2;

    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3
      .select(svgRef.current)
      .attr("viewBox", `0 0 ${size} ${size}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .append("g")
      .attr("transform", `translate(${size / 2}, ${size / 2})`);

    const pie = d3.pie<PieChartData>().value((d) => d.percentage);
    const arc = d3
      .arc<d3.PieArcDatum<PieChartData>>()
      .innerRadius(0)
      .outerRadius(radius * 0.8);

    svg
      .selectAll("path")
      .data(pie(data))
      .enter()
      .append("path")
      .attr("d", arc as any)
      .attr("fill", (_, i) => colors[i])
      .attr("stroke", "none")
      .on("mouseover", (event, d) => showTooltip(d, event.clientX, event.clientY))
      .on("mousemove", (event) => moveTooltip(event.clientX, event.clientY))
      .on("mouseout", hideTooltip);
  }, [data, showTooltip, moveTooltip, hideTooltip]);

  return (
    <Box flex={1} display="flex" flexDirection="column" sx={{ height: "100%" }}>
      {title && (
        <CustomTooltip title={tooltipTitle}>
          <Typography
            variant="h6"
            color="custom.foreground.secondary"
            sx={{ width: "100%" }}
          >
            {title}
          </Typography>
        </CustomTooltip>
      )}

      {showLegend && data?.length > 0 && (
        <Box display="flex" flexWrap="wrap" gap="8px 16px" mt={0.5} mb={1}>
          {data.map((item, i) => (
            <Box
              key={item.label}
              display="flex"
              alignItems="center"
              gap={0.75}
              sx={{ cursor: "default" }}
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: colors[i],
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: labelSize,
                  color: "custom.foreground.secondary",
                  lineHeight: 1,
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {data?.length > 0 ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            zIndex: 2,
          }}
        >
          <svg
            ref={svgRef}
            style={{ width: "60%", height: "100%", zIndex: 3, position: "relative" }}
          />
        </Box>
      ) : (
        <EmptyComponent />
      )}

      <div
        ref={floatingTooltipRef}
        style={{
          position: "fixed",
          display: "none",
          background: "white",
          border: "1px solid #e0e0e0",
          borderRadius: 4,
          padding: "3px 8px",
          fontSize: 12,
          color: "#808080",
          fontWeight: "bold",
          pointerEvents: "none",
          zIndex: 9999,
          boxShadow: "0 2px 6px rgba(0,0,0,0.12)",
          whiteSpace: "nowrap",
        }}
      />
    </Box>
  );
};

export default PieChart;
